import { useState, useEffect } from 'react';
import { XMarkIcon, WrenchScrewdriverIcon, PlusIcon } from '@heroicons/react/24/outline';
import AsyncSelect from 'react-select/async';
import JSONEditor from './JSONEditor';
import api from '../services/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthContext';
import I18nField from './I18nField';
import ThumbnailUpload from './ThumbnailUpload';
import { createI18nObject } from '../utils/i18n';

const nozzleToDot = (key) => key.replace('_', '.');

export default function MachineModelForm({ model, onSave, onCancel, apiPrefix = '/auth/machine-models' }) {
  const isEdit = !!model;
  const isAdmin = apiPrefix !== '/auth/machine-models';
  const { showToast } = useToast();
  const { user } = useAuth();
  const supportedLanguages = user?.supportedLanguages || ['pt'];
  const [systemPresets, setSystemPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(!isEdit);
  const [showForm, setShowForm] = useState(isEdit);
  const [availableNames, setAvailableNames] = useState([]);
  const [loadingNames, setLoadingNames] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    vendor: '',
    description: createI18nObject(supportedLanguages),
    mmsSupport: false,
    hotends: 1,
    costPerHour: '',
    recommendedCostPerHour: '',
    printVolumePerDay: '',
    buildVolume: { x: '', y: '', z: '' },
    settings: {
      '0_2': { inherits: null, override: '{}' },
      '0_4': { inherits: null, override: '{}' },
      '0_6': { inherits: null, override: '{}' },
      '0_8': { inherits: null, override: '{}' },
    },
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [systemSettingsOptions, setSystemSettingsOptions] = useState([]);
  const [nozzleOptions, setNozzleOptions] = useState({
    '0_2': [], '0_4': [], '0_6': [], '0_8': []
  });

  useEffect(() => {
    if (model) {
      const validNozzles = ['0_2', '0_4', '0_6', '0_8'];
      const settings = {};
      validNozzles.forEach(nozzle => {
        const existing = model.settings?.[nozzle];
        settings[nozzle] = {
          inherits: existing?.inherits || null,
          override: existing?.override || '{}',
        };
      });
      setFormData({
        name: model.name || '',
        vendor: model.vendor || '',
        description: model.description || createI18nObject(supportedLanguages),
        mmsSupport: model.mmsSupport || false,
        hotends: model.hotends || 1,
        costPerHour: model.costPerHour || '',
        recommendedCostPerHour: model.recommendedCostPerHour || '',
        printVolumePerDay: model.printVolumePerDay || '',
        buildVolume: model.buildVolume || { x: '', y: '', z: '' },
        settings,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init form when model prop changes
  }, [model]);

  // Fetch available model names from slicer presets
  useEffect(() => {
    setLoadingNames(true);
    api.get('/auth/machine-models/available-names')
      .then(res => setAvailableNames(res.data?.data || []))
      .catch(() => { })
      .finally(() => setLoadingNames(false));
  }, []);

  // Fetch filtered system settings when model name changes
  useEffect(() => {
    const nozzles = ['0_2', '0_4', '0_6', '0_8'];

    const fetchOptions = async (nozzleKey) => {
      const params = { type: 'machine' };
      if (formData.name) {
        params.printerModel = formData.name;
        params.nozzleSize = nozzleToDot(nozzleKey);
      }
      const res = await api.get('/auth/slicer-settings/base', { params });
      return (res.data?.data || []).map(item => ({ value: item._id, label: item.name }));
    };

    if (!formData.name) {
      // No model name — fetch unfiltered once and share
      api.get('/auth/slicer-settings/base', { params: { type: 'machine' } })
        .then(res => {
          const opts = (res.data?.data || []).map(item => ({ value: item._id, label: item.name }));
          setSystemSettingsOptions(opts);
          const shared = {};
          nozzles.forEach(n => { shared[n] = opts; });
          setNozzleOptions(shared);
        })
        .catch(() => { });
    } else {
      // Model name selected — fetch per-nozzle filtered options
      Promise.all(nozzles.map(n => fetchOptions(n)))
        .then(results => {
          const opts = {};
          nozzles.forEach((n, i) => { opts[n] = results[i]; });
          setNozzleOptions(opts);
          const all = [...new Map(results.flat().map(o => [o.value, o])).values()];
          setSystemSettingsOptions(all);
        })
        .catch(() => { });
    }
  }, [formData.name]);

  // Fetch system presets for the picker (create mode only)
  useEffect(() => {
    if (isEdit) return;
    setLoadingPresets(true);
    api.get('/auth/machine-models/by-vendor', { params: { ownership: 'system' } })
      .then(res => {
        const groups = res.data?.data || res.data || [];
        // Only show system presets (user: null)
        const presets = groups.flatMap(g => g.models || []);
        setSystemPresets(presets);
      })
      .catch(() => { })
      .finally(() => setLoadingPresets(false));
  }, [isEdit]);

  const handleSelectPreset = async (preset) => {
    try {
      await api.post(`/auth/machine-models/${preset._id}/duplicate`);
      showToast(`"${preset.fullName || preset.name}" duplicated to your workspace`, 'success');
      onSave();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to duplicate preset', 'error');
    }
  };

  const handleCreateCustom = () => {
    setFormData({
      name: '',
      vendor: '',
      description: createI18nObject(supportedLanguages),
      mmsSupport: false,
      hotends: 1,
      costPerHour: '',
      recommendedCostPerHour: '',
      printVolumePerDay: '',
      buildVolume: { x: '', y: '', z: '' },
      settings: {
        '0_2': { inherits: null, override: '{}' },
        '0_4': { inherits: null, override: '{}' },
        '0_6': { inherits: null, override: '{}' },
        '0_8': { inherits: null, override: '{}' },
      },
    });
    setShowForm(true);
  };

  const handleBackToPresets = () => {
    setShowForm(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBuildVolume = (axis, value) => {
    setFormData(prev => ({
      ...prev,
      buildVolume: { ...prev.buildVolume, [axis]: value },
    }));
  };

  const handleSettingChange = (nozzle, field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [nozzle]: {
          ...prev.settings[nozzle],
          [field]: field === 'override' ? JSON.stringify(value) : value,
        },
      },
    }));
  };

  const createLoadOptions = (nozzleKey) => async (inputValue) => {
    try {
      const params = { q: inputValue, type: 'machine' };
      if (formData.name) {
        params.printerModel = formData.name;
        params.nozzleSize = nozzleToDot(nozzleKey);
      }
      const response = await api.get('/auth/slicer-settings/base', { params });
      return (response.data?.data || []).map(item => ({ value: item._id, label: item.name }));
    } catch {
      return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('vendor', formData.vendor);
      fd.append('description', JSON.stringify(formData.description));
      fd.append('mmsSupport', formData.mmsSupport);
      fd.append('hotends', formData.hotends);
      if (isAdmin) {
        fd.append('recommendedCostPerHour', formData.recommendedCostPerHour);
      } else {
        fd.append('costPerHour', formData.costPerHour);
      }
      if (formData.printVolumePerDay) fd.append('printVolumePerDay', formData.printVolumePerDay);
      if (formData.buildVolume.x && formData.buildVolume.y && formData.buildVolume.z) {
        fd.append('buildVolume', JSON.stringify(formData.buildVolume));
      }
      fd.append('settings', JSON.stringify(formData.settings));
      if (thumbnail) fd.append('thumbnail', thumbnail);

      if (isEdit) {
        await api.patch(`${apiPrefix}/${model._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        showToast('Model updated', 'success');
      } else {
        await api.post(apiPrefix, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        showToast('Model created', 'success');
      }
      onSave();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
  const labelCls = 'block text-sm font-medium text-slate-700 mb-1';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? 'Edit Machine Model' : showForm ? 'New Custom Model' : 'Add Machine Model'}
          </h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        {/* Preset picker (create mode, before form) */}
        {!isEdit && !showForm && (
          <div className="flex-1 overflow-y-auto p-5">
            {loadingPresets ? (
              <p className="text-sm text-slate-500 text-center py-8">Loading presets...</p>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-4">Select a system preset to start from, or create a fully custom model.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  {systemPresets.map(preset => (
                    <button
                      key={preset._id}
                      type="button"
                      onClick={() => handleSelectPreset(preset)}
                      className="border border-slate-200 rounded-lg overflow-hidden text-left hover:border-blue-400 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <div className="h-24 bg-slate-100 flex items-center justify-center">
                        {preset.thumbnail?.url ? (
                          <img src={preset.thumbnail.url} alt={preset.name} className="h-full w-full object-cover" />
                        ) : (
                          <WrenchScrewdriverIcon className="h-8 w-8 text-slate-300" />
                        )}
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-semibold text-slate-900 truncate">{preset.fullName || preset.name}</p>
                        {preset.buildVolume && (
                          <p className="text-[11px] text-slate-400 mt-0.5">{preset.buildVolume.x}x{preset.buildVolume.y}x{preset.buildVolume.z} mm</p>
                        )}
                        {preset.costPerHour != null && (
                          <p className="text-[11px] text-slate-400">{preset.costPerHour} &euro;/h</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleCreateCustom}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-slate-600 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  Create custom model
                </button>
              </>
            )}
          </div>
        )}

        {/* Form (edit mode or after preset selection) */}
        {showForm && (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
            {!isEdit && (
              <button type="button" onClick={handleBackToPresets} className="text-xs text-blue-600 hover:text-blue-800 mb-2">
                &larr; Back to presets
              </button>
            )}

            <div>
              <label className={labelCls}>Name *</label>
              <select required value={formData.name} onChange={e => handleChange('name', e.target.value)} className={inputCls} disabled={loadingNames}>
                <option value="">{loadingNames ? 'Loading...' : 'Select a model name'}</option>
                {availableNames.map(n => <option key={typeof n === 'object' ? n._id : n} value={typeof n === 'object' ? n.name : n}>{typeof n === 'object' ? n.name : n}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">Available model names are loaded from slicer <code className="text-xs bg-slate-100 px-1 rounded">machine_model</code> settings.</p>
            </div>

            <div>
              <label className={labelCls}>Vendor</label>
              <input type="text" value={formData.vendor} onChange={e => handleChange('vendor', e.target.value)} className={inputCls} />
            </div>

            {/* Description */}
            <I18nField
              label="Description"
              value={formData.description}
              onChange={(lang, value) => setFormData(prev => ({
                ...prev,
                description: { ...prev.description, [lang]: value }
              }))}
              languages={supportedLanguages}
              type="textarea"
              disabled={saving}
              rows={3}
              maxLength={1000}
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>{isAdmin ? 'Recommended Cost/Hour *' : 'Cost/Hour *'}</label>
                <input type="number" step="0.01" min="0" required value={isAdmin ? formData.recommendedCostPerHour : formData.costPerHour} onChange={e => handleChange(isAdmin ? 'recommendedCostPerHour' : 'costPerHour', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Hotends</label>
                <input type="number" min={1} value={formData.hotends} onChange={e => handleChange('hotends', parseInt(e.target.value) || 1)} className={inputCls} />
              </div>
            </div>

            <div>
              <label className={labelCls}>Print Volume/Day (cm³)</label>
              <input type="number" min="0" step="1" value={formData.printVolumePerDay} onChange={e => handleChange('printVolumePerDay', e.target.value)} className={inputCls} placeholder="Optional daily capacity" />
            </div>

            <div>
              <label className={labelCls}>Build Volume (mm)</label>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" placeholder="X" value={formData.buildVolume.x} onChange={e => handleBuildVolume('x', e.target.value)} className={inputCls} />
                <input type="number" placeholder="Y" value={formData.buildVolume.y} onChange={e => handleBuildVolume('y', e.target.value)} className={inputCls} />
                <input type="number" placeholder="Z" value={formData.buildVolume.z} onChange={e => handleBuildVolume('z', e.target.value)} className={inputCls} />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.mmsSupport} onChange={e => handleChange('mmsSupport', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-slate-700">Multi-material support</span>
              </label>
            </div>

            {/* Nozzle Settings */}
            <div className="space-y-3 border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold text-slate-900">Nozzle Settings</h3>
              <p className="text-xs text-slate-500">Configure base slicer settings per nozzle size. Each slot can inherit from a system setting with optional overrides.</p>
              {[
                { key: '0_2', label: '0.2' },
                { key: '0_4', label: '0.4' },
                { key: '0_6', label: '0.6' },
                { key: '0_8', label: '0.8' },
              ].map(({ key, label }) => {
                const setting = formData.settings?.[key] || { inherits: null, override: '{}' };
                let overrideData = {};
                try { overrideData = setting.override ? JSON.parse(setting.override) : {}; } catch { /* ignore parse errors */ }

                return (
                  <div key={key} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <label className="block text-xs font-semibold text-slate-700 mb-2">Nozzle {label}mm</label>
                    <div className="mb-2">
                      <label className="block text-xs text-slate-600 mb-1">Inherits from</label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={nozzleOptions[key] || systemSettingsOptions}
                        isClearable
                        loadOptions={createLoadOptions(key)}
                        value={
                          setting.inherits
                            ? (nozzleOptions[key] || systemSettingsOptions).find(opt => opt.value === setting.inherits)
                            || { value: setting.inherits, label: setting.inherits }
                            : null
                        }
                        onChange={(val) => handleSettingChange(key, 'inherits', val?.value || null)}
                        placeholder="Search system setting..."
                        isDisabled={saving}
                        className="text-sm"
                        key={`${key}-${formData.name || 'none'}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1">Override (JSON)</label>
                      <JSONEditor
                        data={overrideData}
                        onChange={(val) => handleSettingChange(key, 'override', val)}
                        disabled={saving}
                        minHeight={80}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <ThumbnailUpload
              currentUrl={model?.thumbnail?.url}
              file={thumbnail}
              onChange={setThumbnail}
              disabled={saving}
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
